import env from '@beam-australia/react-env';
import React from 'react';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';

// Cloudinary doesn't have great typescript support yet, so we have to shim
// definitions for the time being.

export type UploadSuccessInfo = {
  asset_id: string;
  batchId: string;
  bytes: number;
  created_at: string;
  etag: string;
  folder: string;
  format: string;
  height: number;
  id: string;
  original_filename: string;
  placeholder: boolean;
  public_id: string;
  resource_type: string;
  secure_url: string;
  signature: string;
  tags: string[];
  thumbnail_url: string;
  type: string;
  url: string;
  version: number;
  version_id: string;
  width: number;
};

type CloudinaryUploadWidgetProps = {
  children: React.ReactNode;
  onSuccess?: (e: UploadSuccessInfo) => void;
};

type CloudinaryWidget = {
  destroy: () => void;
  open: () => void;
};

declare const window: Window &
  typeof globalThis & {
    cloudinary: {
      createUploadWidget: (
        // https://cloudinary.com/documentation/upload_widget_reference#client_side_parameters
        config: {
          cloudName: string;
          uploadPreset: string;
          folder: string;
          multiple: boolean;
          sources: string[];
          clientAllowedFormats: string[];
        },
        callback: (
          error: string | null,
          result: {
            event:
              | 'abort'
              | 'batch-cancelled'
              | 'close'
              | 'display-changed'
              | 'publicid'
              | 'queues-end'
              | 'queues-start'
              | 'retry'
              | 'show-completed'
              | 'source-changed'
              | 'success'
              | 'tags'
              | 'upload-added';
            info: UploadSuccessInfo;
          },
        ) => void,
      ) => CloudinaryWidget;
    };
  };

function CloudinaryUploadWidget({
  children,
  onSuccess = () => {},
}: CloudinaryUploadWidgetProps) {
  const [widget, setWidget] = React.useState<undefined | CloudinaryWidget>();
  const ownerOrgId = useOwnerOrgId();

  React.useEffect(() => {
    const config = {
      cloudName: env('CLOUDINARY_CLOUD_NAME'),
      uploadPreset: env('CLOUDINARY_UPLOAD_PRESET'),
      folder: `${env('CLOUDINARY_ENV')}/${ownerOrgId}`,
      multiple: false,
      sources: ['local', 'url', 'camera', 'image_search'],
      clientAllowedFormats: ['image'],
    };
    const newWidget = window.cloudinary.createUploadWidget(
      config,
      (error, result) => {
        if (result.event === 'success') {
          onSuccess(result.info);
        }
      },
    );
    setWidget(newWidget);
    return () => {
      if (!widget) return;
      widget.destroy();
    };
  }, [ownerOrgId]);

  const showWidget = () => {
    if (!widget) return;
    widget.open();
  };

  return (
    <button onClick={showWidget} type="button">
      {children}
    </button>
  );
}

export default CloudinaryUploadWidget;
